import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { GeneralSigninPrompt } from '@/components/global-nav/signin-prompt/general-signin-prompt';
import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useGlobalNavContext } from '@/patterns/global-nav/src/nav-links-container';

import { messages } from '../messages';

export const LoggedOutUser = ({
	onLogInClick,
	logInHref,
	isUseGeneralSigninPromptConfig = false,
	setIsUseGeneralSigninPromptConfig,
}: {
	readonly onLogInClick: () => void;
	readonly logInHref: string;
	readonly isUseGeneralSigninPromptConfig?: boolean;
	readonly setIsUseGeneralSigninPromptConfig?: Dispatch<SetStateAction<boolean>>;
}) => {
	const { isDarkTheme } = useTheme();
	const { dropdownRefCurrentNode } = useGlobalNavContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (createAnalyticsEvent && isUseGeneralSigninPromptConfig) {
			createAnalyticsEvent({
				eventName: 'gwp.generalSigninPrompt.modalDisplayed',
				eventType: 'ui',
				action: 'displayed',
				actionSubject: 'modal',
				actionSubjectId: 'GeneralSigninPromptModalDisplayed',
			}).fire();
		}
	}, [createAnalyticsEvent, isUseGeneralSigninPromptConfig]);

	return (
		<>
			<a
				href={logInHref}
				onClick={onLogInClick}
				css={[signInLinkStyle, isDarkTheme && darkSignInLinkStyle]}
			>
				{isUseGeneralSigninPromptConfig ? (
					<FormattedMessage {...messages.logInAlternative} />
				) : (
					<FormattedMessage {...messages.logIn} />
				)}
			</a>
			{!dropdownRefCurrentNode && (
				<GeneralSigninPrompt
					isUseGeneralSigninPromptConfig={isUseGeneralSigninPromptConfig}
					setIsUseGeneralSigninPromptConfig={setIsUseGeneralSigninPromptConfig}
				/>
			)}
		</>
	);
};

// Compiled accepts the WAC brand colors needed to match the other nav links.
const signInLinkStyle = css({
	display: 'flex',
	alignItems: 'center',
	paddingBlockStart: token('space.100'),
	paddingBlockEnd: token('space.100'),
	fontFamily: 'inherit',
	fontSize: '1rem',
	fontWeight: token('font.weight.medium'),
	lineHeight: '1rem',
	color: wacToken('color.primary.black'),
	textDecoration: 'none',
	whiteSpace: 'nowrap',
	'&:hover': {
		color: wacToken('color.primary.blue'),
		textDecoration: 'none',
	},
	'&:focus-visible': { outlineOffset: '-2px' },
});

const darkSignInLinkStyle = css({
	color: wacToken('color.neutral.light0'),
	'&:hover': { color: wacToken('color.extended.blue200') },
});
